<template>
  <div class="slow-bills">
    <div class="slow-bills__bar">
      <div class="buttons has-addons">
        <button class="button is-small seg" :class="{'seg--on': kind === 'open'}" @click="setKind('open')">{{ $t('deliverySpeed.slow_open_tab') }}</button>
        <button class="button is-small seg" :class="{'seg--on': kind === 'late'}" @click="setKind('late')">{{ $t('deliverySpeed.slow_late_tab') }}</button>
      </div>
      <div v-if="kind === 'open'" class="select is-small">
        <select v-model.number="minAge" :aria-label="$t('deliverySpeed.min_age_label')" @change="reload()">
          <option :value="0">{{ $t('deliverySpeed.min_age_any') }}</option>
          <option v-for="days in [8, 15, 31, 61]" :key="days" :value="days">{{ $t('deliverySpeed.min_age_over', {days}) }}</option>
        </select>
      </div>
      <span v-if="focus.stage" class="tag is-medium">
        {{ $t('deliverySpeed.stage_' + focus.stage) }}
        <button class="delete is-small" :aria-label="$t('deliverySpeed.clear_filter')" @click="$emit('clear-focus')"></button>
      </span>
      <span v-if="focus.position_id" class="tag is-medium">
        {{ focus.position_name }}
        <button class="delete is-small" :aria-label="$t('deliverySpeed.clear_filter')" @click="$emit('clear-focus')"></button>
      </span>
      <span class="slow-bills__total">{{ $t('deliverySpeed.slow_total', {total: fmt(total)}) }}</span>
      <button class="button is-small is-primary is-rounded" :disabled="!rows.length || exporting" :class="{'is-loading': exporting}" @click="exportExcel()">
        <span class="icon"><i class="fal fa-file-excel"></i></span>
        <span>{{ $t('deliverySpeed.export_excel') }}</span>
      </button>
    </div>
    <p class="delivery-note">{{ $t(kind === 'open' ? 'deliverySpeed.slow_note_open' : 'deliverySpeed.slow_note_late') }}</p>
    <p v-if="exportCapped" class="delivery-note">{{ $t('deliverySpeed.export_limit', {max: fmt(exportRows), total: fmt(total)}) }}</p>

    <div v-if="error" class="notification is-warning is-light">{{ $t('deliverySpeed.unavailable_' + error) }}</div>
    <div v-else class="table-container">
      <table class="table is-fullwidth is-hoverable is-narrow">
        <thead>
          <tr>
            <th>{{ $t('deliverySpeed.col_bill') }}</th>
            <th>{{ $t('deliverySpeed.col_route') }}</th>
            <th v-if="!filters.lane">{{ $t('deliverySpeed.col_lane') }}</th>
            <th>{{ $t('deliverySpeed.col_created') }}</th>
            <th class="has-text-right">{{ $t(kind === 'open' ? 'deliverySpeed.col_age' : 'deliverySpeed.col_took') }}</th>
            <th>{{ $t(kind === 'open' ? 'deliverySpeed.col_status' : 'deliverySpeed.col_slowest') }}</th>
            <th v-if="kind === 'open'">{{ $t('deliverySpeed.col_last_scan') }}</th>
            <th>{{ $t('deliverySpeed.col_flags') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="bill in rows" :key="bill.shipment_id">
            <td>
              <a @click="$router.push({name: 'list.shipment.detail', params: {id: bill.shipment_id}})">{{ bill.shipment_number }}</a>
            </td>
            <td>{{ bill.start_branch }} → {{ bill.end_branch }}</td>
            <td v-if="!filters.lane">{{ $t('deliverySpeed.lane_' + bill.lane) }}</td>
            <td>{{ date(bill.created_at) }}</td>
            <td class="has-text-right">
              <strong>{{ kind === 'open' ? bill.age_days : bill.arrive_days }}</strong>
              <small> / {{ bill.sla_days }}</small>
            </td>
            <td v-if="kind === 'open'">
              {{ $t('deliverySpeed.stage_' + bill.stage) }}<span v-if="bill.position_name">: {{ bill.position_name }}</span>
            </td>
            <td v-else>{{ bill.slowest_leg ? $t('deliverySpeed.leg_' + bill.slowest_leg) : '-' }}</td>
            <td v-if="kind === 'open'">{{ $t('deliverySpeed.idle_days', {days: bill.idle_days}) }}</td>
            <td>
              <span v-if="bill.is_cod" class="tag is-light">{{ $t('deliverySpeed.tag_cod') }}</span>
              <span v-if="bill.has_wrong_branch" class="tag is-light">{{ $t('deliverySpeed.flag_wrong_branch') }}</span>
              <span v-if="bill.has_failed_attempt" class="tag is-light">{{ $t('deliverySpeed.flag_failed_attempt') }}</span>
              <span v-if="bill.has_dest_change" class="tag is-light">{{ $t('deliverySpeed.flag_dest_change') }}</span>
            </td>
          </tr>
          <tr v-if="!rows.length && !loading">
            <td :colspan="(kind === 'open' ? 7 : 6) + (filters.lane ? 0 : 1)" class="has-text-centered">{{ $t('deliverySpeed.slow_empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pages > 1" class="slow-bills__pager">
      <button class="button is-small" :disabled="page <= 1 || loading" @click="go(page - 1)">{{ $t('deliverySpeed.prev') }}</button>
      <span>{{ $t('deliverySpeed.page_of', {page, pages}) }}</span>
      <button class="button is-small" :disabled="page >= pages || loading" @click="go(page + 1)">{{ $t('deliverySpeed.next') }}</button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import XLSX from 'xlsx';
import {fetchDeliverySpeedSlowBills, reasonOf} from '@/services/deliverySpeedService';

const PER_PAGE = 50;
const EXPORT_ROWS = 5000;

export default {
  name: 'SlowBills',
  props: {
    // {period, lane, province_id, shipment_type} from the page
    filters: {type: Object, required: true},
    // {stage} or {position_type, position_store, position_id, position_name} from a click above
    focus: {type: Object, default: () => ({})},
  },
  data() {
    return {
      kind: 'open',
      minAge: 0,
      page: 1,
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      exportRows: EXPORT_ROWS,
      error: null,
      requestSeq: 0,
    };
  },
  computed: {
    pages() {
      return Math.max(1, Math.ceil(this.total / PER_PAGE));
    },
    // Picking a lane changes the filters and clears the focus in one tick: one request.
    scopeKey() {
      return JSON.stringify([this.filters, this.focus]);
    },
    hasFocus() {
      return Boolean(this.focus.stage || this.focus.position_id);
    },
    exportCapped() {
      return this.total > EXPORT_ROWS;
    },
  },
  watch: {
    scopeKey() {
      // a stage or position describes overdue bills, so it always opens the overdue list
      if (this.hasFocus) this.kind = 'open';
      this.reload();
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    query(extra = {}) {
      const params = {...this.filters, kind: this.kind, ...extra};
      if (this.focus.stage) params.stage = this.focus.stage;
      if (this.focus.position_id) {
        params.position_type = this.focus.position_type;
        params.position_store = this.focus.position_store;
        params.position_id = this.focus.position_id;
      }
      if (this.kind === 'open' && this.minAge) params.min_age_days = this.minAge;
      Object.keys(params).forEach((key) => (params[key] === null || params[key] === undefined || params[key] === '') && delete params[key]);
      return params;
    },
    async fetch() {
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const result = await fetchDeliverySpeedSlowBills(this.query({page: this.page, per_page: PER_PAGE}));
        if (seq !== this.requestSeq) return;
        this.rows = result.data;
        this.total = result.total;
        this.error = null;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.rows = [];
        this.total = 0;
        this.error = reasonOf(e);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    reload() {
      this.page = 1;
      this.fetch();
    },
    setKind(kind) {
      if (this.kind === kind) return;
      this.kind = kind;
      if (kind === 'late' && this.hasFocus) {
        this.$emit('clear-focus'); // the parent's change reloads the list
        return;
      }
      this.reload();
    },
    go(page) {
      this.page = page;
      this.fetch();
    },
    async exportExcel() {
      this.exporting = true;
      // the file must match what was asked for, even if the tab changes while it downloads
      const kind = this.kind;
      try {
        const result = await fetchDeliverySpeedSlowBills(this.query({page: 1, per_page: EXPORT_ROWS}));
        const t = (key) => this.$t('deliverySpeed.' + key);
        const sheet = XLSX.utils.json_to_sheet(result.data.map((bill) => ({
          [t('col_bill')]: bill.shipment_number,
          [t('col_origin')]: bill.start_branch,
          [t('col_destination')]: bill.end_branch,
          [t('col_lane')]: this.$t('deliverySpeed.lane_' + bill.lane),
          [t('col_created')]: bill.created_at,
          [t('col_target_days')]: bill.sla_days,
          [t('col_age')]: bill.age_days,
          [t('col_took')]: bill.arrive_days,
          [t('col_status')]: this.$t('deliverySpeed.stage_' + bill.stage),
          [t('col_position')]: bill.position_name,
          [t('col_last_scan')]: bill.last_event_at,
          [t('col_slowest')]: bill.slowest_leg ? this.$t('deliverySpeed.leg_' + bill.slowest_leg) : '',
          [t('tag_cod')]: bill.is_cod ? 1 : 0,
          [t('flag_wrong_branch')]: bill.has_wrong_branch ? 1 : 0,
          [t('flag_failed_attempt')]: bill.has_failed_attempt ? 1 : 0,
          [t('flag_dest_change')]: bill.has_dest_change ? 1 : 0,
        })));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, kind);
        XLSX.writeFile(book, 'delivery-speed-' + kind + '-' + moment().format('YYYY-MM-DD') + '.xlsx');
      } catch (e) {
        this.error = reasonOf(e);
      } finally {
        this.exporting = false;
      }
    },
    fmt(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },
    date(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.slow-bills__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  .buttons {
    margin-bottom: 0;

    .button {
      margin-bottom: 0;
    }
  }
}

.seg {
  background: #ffffff;
  color: #52514e;
  border-color: #c3c2b7;

  &--on {
    background: #2a78d6;
    border-color: #2a78d6;
    color: #ffffff;
  }
}

.slow-bills__bar .select,
.slow-bills__bar .select select {
  width: auto;
}

.slow-bills__total {
  margin-left: auto;
  font-size: 0.85rem;
  color: #52514e;
}

.slow-bills__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 0.85rem;
  padding-top: 8px;
}

.table {
  th,
  td {
    white-space: nowrap;
    font-size: 0.85rem;
  }

  .tag {
    margin-right: 4px;
  }
}

.delivery-note {
  font-size: 0.8rem;
  color: #52514e;
  margin-bottom: 8px;
  text-align: left;
}
</style>
